/* eslint-disable no-continue */
import { useBreakpoint } from '@chakra-ui/react';
import { breakpoints as defaultBreakPoints } from '@chakra-ui/utils';
import { useTheme } from '@emotion/react';
import isObject from 'lodash-es/isObject';
import type {
    ResponsiveArray,
    ResponsiveObject,
    ResponsiveValue,
    ThemeTypings,
} from '@chakra-ui/react';
import type { AnalyzeBreakpointsReturn } from '@chakra-ui/utils';

interface UseBreakpointOptions {
    fallback?: string;
}

/**
 * React hook for getting the values for the current breakpoint from multiple
 * provided responsive values objects.
 *
 * @example
 * const { width, height, color } = useBreakpointValues({
 *   width: { base: '150px', md: '250px' },
 *   height: { base: '100px', md: '200px' },
 *   color: { base: 'red', md: 'blue' }
 * })
 *
 * Based off original implementation from Chakra UI:
 * @see https://github.com/chakra-ui/chakra-ui/blob/v2/packages/components/src/media-query/use-breakpoint-value.ts
 * Also from techniques in:
 * @see https://github.com/chakra-ui/chakra-ui/blob/v2/packages/styled-system/src/utils/expand-responsive.ts
 */
export function useBreakpointValues<V>(
    values: { [K in keyof V]: ResponsiveValue<V[K]> },
    arg?: UseBreakpointOptions | string,
): { [K in keyof V]: V[K] } {
    const opts = isObject(arg) ? arg : { fallback: arg ?? 'base' };
    const breakpoint = useBreakpoint(opts);
    const theme = useTheme();

    if (!breakpoint) {
        throw new Error('No breakpoint found');
    }

    const analizedBreakpoints =
        '__breakpoints' in theme ? (theme.__breakpoints as AnalyzeBreakpointsReturn) : null;

    if (!analizedBreakpoints) {
        throw new Error('Theme not set correctly');
    }

    const { keys } = analizedBreakpoints;
    const breakpointsSet = keys as Set<ThemeTypings['breakpoints']>;
    const breakpoints = Array.from(breakpointsSet || []);

    const result: Map<string, V[keyof V]> = new Map();

    // eslint-disable-next-line no-restricted-syntax
    for (const key in values) {
        if (Object.prototype.hasOwnProperty.call(values, key)) {
            const responsiveValue = values[key];

            if (Array.isArray(responsiveValue)) {
                const responsiveObject = arrayToObjectNotation(responsiveValue, breakpoints);
                const val = getClosestOrDefault<V[keyof V]>(
                    responsiveObject,
                    breakpoint,
                    breakpoints,
                );
                if (val !== undefined) {
                    result.set(key, val);
                    continue;
                }
            }

            if (!isObject(responsiveValue) && !isResponsive(responsiveValue, breakpointsSet)) {
                result.set(key, responsiveValue as V[keyof V]);
                continue;
            }

            if (
                isObject(responsiveValue) &&
                isResponsive<V[keyof V]>(responsiveValue, breakpointsSet)
            ) {
                const val = getClosestOrDefault(responsiveValue, breakpoint, breakpoints);
                if (val !== undefined) {
                    result.set(key, val);
                    continue;
                }
            }

            throw new Error('No default value could be gathered');
        }
    }

    return Object.fromEntries(result) as { [K in keyof V]: V[K] };
}

function getClosestValue<T>(
    values: ResponsiveObject<T>,
    breakpoint: string,
    breakpoints = defaultBreakPoints,
): T | undefined {
    let index = Object.keys(values).indexOf(breakpoint);

    if (index !== -1) {
        return values[breakpoint];
    }

    let stopIndex = breakpoints.indexOf(breakpoint);

    while (stopIndex >= 0) {
        const key = breakpoints[stopIndex];

        if (Object.prototype.hasOwnProperty.call(values, key)) {
            index = stopIndex;
            break;
        }
        stopIndex -= 1;
    }

    if (index !== -1) {
        const key = breakpoints[index];
        return values[key];
    }

    return undefined;
}

const getClosestOrDefault = <T>(
    values: Partial<Record<string, T>>,
    breakpoint: string,
    breakpoints = defaultBreakPoints,
): T | undefined => getClosestValue(values, breakpoint, breakpoints) ?? values[breakpoint];

export function arrayToObjectNotation<T>(
    values: ResponsiveArray<T>,
    bps: ThemeTypings['breakpoints'][],
): Partial<Record<string, T>> {
    const result = {} as ResponsiveObject<T>;
    values.forEach((value, index) => {
        const key = bps[index];
        if (value == null) return;
        result[key] = value;
    });
    return result;
}

export function isResponsive<T>(
    test: unknown,
    breakpoints: Set<ThemeTypings['breakpoints']>,
): test is Partial<Record<string, T>> {
    const keys = isObject(test) ? Object.keys(test) : [];
    return keys.length > 0 && keys.every((key) => breakpoints.has(key));
}
